/*
  Sleep Kit widget (snippets/sleep-kit.liquid).
  Two tabs: Single shows the theme's Add to Cart, Sleep Kit shows the kit.
  The kit adds the main item and every kit item to the cart in one request.
  Selleasy applies the kit discount in the cart, and only when every kit item
  is in the cart, so the customer cannot remove an item from the kit.

  The main item can be any colour product from the "set" collection. A colour
  swatch changes the kit only, not the page. The size picker above the kit
  sets the size, and the kit keeps that size in the colour picked here.
*/
if (!customElements.get('sleep-kit')) {
  customElements.define('sleep-kit', class SleepKit extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-sleep-kit-config]').textContent);
      this.form = document.getElementById(this.dataset.formId);
      this.productForm = this.form ? this.form.closest('product-form') : null;

      // The current product is not in its own set collection: show the theme's Add to Cart only.
      if (!this.config.mainVariants[this.config.mainVariantId]) {
        this.hidden = true;
        document.querySelectorAll('[data-sleep-kit-preload]').forEach((style) => style.remove());
        return;
      }

      this.mainVariantId = this.config.mainVariantId;
      this.mainProductId = this.variant(this.mainVariantId).productId;

      this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
      this.panel = this.querySelector('.sleep-kit__panel');
      this.mainSwatches = this.querySelector('[data-main-swatches]');
      this.main = {
        el: this.querySelector('.sleep-kit__item--main'),
        image: this.querySelector('[data-main-image]'),
        name: this.querySelector('[data-main-name]'),
        select: this.querySelector('[data-main-select]')
      };
      this.addons = Array.from(this.querySelectorAll('[data-addon]')).map((el) => ({
        el,
        data: this.config.addons[parseInt(el.dataset.addonIndex, 10)],
        variantInput: el.querySelector('[data-addon-variant]'),
        colourLabel: el.querySelector('[data-addon-colour]'),
        image: el.querySelector('[data-addon-image]')
      }));
      this.button = this.querySelector('[data-add]');
      this.buttonText = this.querySelector('[data-button-text]');
      this.spinner = this.querySelector('.sleep-kit__spinner');
      this.error = this.querySelector('[data-error]');
      this.total = this.querySelector('[data-total]');
      this.totalCompare = this.querySelector('[data-total-compare]');

      this.addEventListener('click', this.onClick.bind(this));
      this.button.addEventListener('click', this.onAdd.bind(this));
      // The size dropdown changes the kit only, the same as the colour swatches.
      this.main.select.addEventListener('change', () => {
        this.setMainVariant(parseInt(this.main.select.value, 10));
        this.render();
      });
      this.main.select.dataset.productId = this.mainProductId;

      // theme.js (VariantSelects.updateVariantInput) sets the form's id input
      // and fires a bubbling change event when the customer picks a new size.
      if (this.form) {
        this.form.addEventListener('change', (event) => {
          if (event.target.name !== 'id') return;
          this.onPageVariantChange(parseInt(event.target.value, 10));
        });
        const idInput = this.form.querySelector('[name="id"]');
        if (idInput && this.variant(idInput.value)) this.setMainVariant(parseInt(idInput.value, 10));
      }

      this.selectTab(this.dataset.defaultTab);
      // From now on this element shows and hides the theme's Add to Cart.
      document.querySelectorAll('[data-sleep-kit-preload]').forEach((style) => style.remove());
      this.render();
    }

    variant(id) {
      return this.config.mainVariants[id];
    }

    variantsOf(productId) {
      return Object.keys(this.config.mainVariants)
        .map((id) => ({ id: parseInt(id, 10), ...this.config.mainVariants[id] }))
        .filter((variant) => variant.productId === productId);
    }

    // Best variant of a colour product for a size: same size in stock, else
    // any size in stock, else the same size (sold out).
    variantForSize(productId, sizeKey) {
      const variants = this.variantsOf(productId);
      return variants.find((v) => v.sizeKey === sizeKey && v.available)
        || variants.find((v) => v.available)
        || variants.find((v) => v.sizeKey === sizeKey)
        || variants[0];
    }

    onPageVariantChange(id) {
      const pageVariant = this.variant(id);
      if (!pageVariant) return;
      // The customer picked another colour in the kit: keep it, and use the new size.
      const target = this.mainProductId === pageVariant.productId
        ? pageVariant
        : this.variantForSize(this.mainProductId, pageVariant.sizeKey);
      this.setMainVariant(target.id);
      this.render();
    }

    onClick(event) {
      const tab = event.target.closest('[data-tab]');
      if (tab) {
        this.selectTab(tab.dataset.tab);
        this.track('sleep_kit_tab', { tab: tab.dataset.tab });
        return;
      }

      const swatch = event.target.closest('.sleep-kit__swatch');
      if (!swatch || swatch.disabled) return;

      if (this.mainSwatches && this.mainSwatches.contains(swatch)) {
        const productId = parseInt(swatch.dataset.productId, 10);
        const target = this.variantForSize(productId, this.variant(this.mainVariantId).sizeKey);
        if (target) this.setMainVariant(target.id);
        this.render();
        return;
      }

      const addon = this.addons.find((item) => item.el.contains(swatch));
      if (!addon) return;
      this.selectAddonVariant(addon, swatch.dataset.variantId);
      this.render();
    }

    selectTab(name) {
      const isKit = name === 'kit';
      this.tabs.forEach((tab) => {
        const selected = tab.dataset.tab === name;
        tab.setAttribute('aria-selected', selected);
        tab.classList.toggle('is-selected', selected);
      });
      this.panel.hidden = !isKit;
      if (this.productForm) this.productForm.classList.toggle('sleep-kit-hidden', isKit);
    }

    setMainVariant(id) {
      const before = (this.config.defaults[this.mainVariantId] || []).join(',');
      const after = this.config.defaults[id] || [];
      this.mainVariantId = id;
      this.mainProductId = this.variant(id).productId;
      // A size change keeps the same defaults, so the customer's own picks stay.
      // A colour change gets new defaults from the colour map, so apply them.
      if (after.join(',') === before) return;
      this.addons.forEach((addon, index) => {
        if (addon.data.variants[after[index]]) this.selectAddonVariant(addon, after[index]);
      });
    }

    selectAddonVariant(addon, variantId) {
      const variant = addon.data.variants[variantId];
      if (!variant) return;
      addon.variantInput.value = variantId;
      if (addon.colourLabel) addon.colourLabel.textContent = variant.title;
      if (addon.image && variant.image) addon.image.src = variant.image;
      this.markSelected(addon.el, (swatch) => swatch.dataset.variantId === String(variantId));
    }

    markSelected(container, isSelected) {
      container.querySelectorAll('.sleep-kit__swatch').forEach((swatch) => {
        const selected = isSelected(swatch);
        swatch.classList.toggle('is-selected', selected);
        swatch.setAttribute('aria-checked', selected);
      });
    }

    addonVariant(addon) {
      return addon.data.variants[addon.variantInput.value];
    }

    discountFor(price) {
      // Round down per item, so the price shown is never lower than the cart price.
      return Math.floor((price * this.config.percent) / 100);
    }

    render() {
      const main = this.variant(this.mainVariantId);
      const lines = [{ el: this.main.el, variant: main }].concat(
        this.addons.map((addon) => ({ el: addon.el, variant: this.addonVariant(addon) }))
      );
      const available = lines.every((line) => line.variant && line.variant.available);

      this.main.name.textContent = this.config.mainTitle || main.productTitle;
      this.renderMainSelect(main);
      if (this.main.image && main.image) this.main.image.src = main.image;
      if (this.mainSwatches) {
        this.markSelected(this.mainSwatches, (swatch) => swatch.dataset.productId === String(main.productId));
      }

      let subtotal = 0;
      let discount = 0;
      lines.forEach((line) => {
        const price = line.variant ? line.variant.price : 0;
        line.el.querySelector('[data-price]').textContent = this.formatMoney(price - this.discountFor(price));
        line.el.querySelector('[data-compare]').textContent = this.formatMoney(price);
        subtotal += price;
        discount += this.discountFor(price);
      });

      this.total.textContent = this.formatMoney(subtotal - discount);
      this.totalCompare.textContent = this.formatMoney(subtotal);
      this.buttonText.textContent = available
        ? this.config.strings.kitButton.replace('[saving]', this.formatMoney(discount))
        : this.config.strings.soldOut;
      this.button.disabled = !available;
    }

    // Rebuild the size options when the kit shows another colour product.
    renderMainSelect(main) {
      const select = this.main.select;
      if (select.dataset.productId !== String(main.productId)) {
        select.innerHTML = '';
        this.variantsOf(main.productId).forEach((variant) => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.disabled = !variant.available;
          option.textContent = variant.available ? variant.title : `${variant.title} - ${this.config.strings.soldOut}`;
          select.appendChild(option);
        });
        select.dataset.productId = main.productId;
        // A product with one variant has nothing to choose.
        select.hidden = select.options.length < 2;
      }
      select.value = String(this.mainVariantId);
    }

    async onAdd() {
      if (this.button.getAttribute('aria-busy') === 'true') return;

      // Same line property as the Selleasy widget: "<main product id>#offer#<offer id>".
      const properties = this.config.offerId
        ? { '_lb-product': `${this.mainProductId}#offer#${this.config.offerId}` }
        : undefined;
      const items = [{ id: this.mainVariantId, quantity: 1, properties }].concat(
        this.addons.map((addon) => ({ id: parseInt(addon.variantInput.value, 10), quantity: 1, properties }))
      );

      this.showError(false);
      this.button.setAttribute('aria-busy', 'true');
      this.spinner.hidden = false;

      try {
        const response = await this.postJSON(`${window.routes.cart_add_url}.js`, { items });
        if (response.status) {
          this.showError(response.description || this.config.strings.error);
          return;
        }
        // The items are in the cart. A failed discount code must not stop the cart from opening.
        try {
          await this.applyDiscountCode();
        } catch (error) {
          console.error('Sleep Kit: discount code not applied', error);
        }
        this.track('sleep_kit_add_to_cart', { kit_name: this.config.kitName, item_count: items.length });
        this.openCart();
      } catch (error) {
        console.error(error);
        this.showError(this.config.strings.error);
      } finally {
        this.button.removeAttribute('aria-busy');
        this.spinner.hidden = true;
      }
    }

    // Selleasy gives the kit discount as a discount code on the cart. The
    // "discount" field of /cart/update.js replaces all codes, so send the
    // codes that are already on the cart too.
    async applyDiscountCode() {
      const code = this.config.discountCode;
      if (!code) return;

      const cart = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart.js`, {
        headers: { Accept: 'application/json' }
      }).then((response) => response.json());
      const codes = (cart.discount_codes || []).map((discount) => discount.code);
      if (codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) return;

      codes.push(code);
      await this.postJSON(`${window.routes.cart_update_url}.js`, { discount: codes.join(',') });
    }

    postJSON(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then((response) => response.json());
    }

    openCart() {
      // Same method as product-form.js: the header cart link opens and refreshes the drawer.
      if (window.jQuery && document.querySelector('.js-drawer-open-right')) {
        window.jQuery('.js-drawer-open-right').trigger('click');
      } else {
        window.location.href = this.config.cartUrl;
      }
    }

    showError(message) {
      this.error.hidden = !message;
      this.error.textContent = message || '';
    }

    track(name, params) {
      // GA4 and Clarity replace the Selleasy widget reports.
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      if (typeof window.clarity === 'function') window.clarity('event', name);
    }

    formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, this.config.moneyFormat);
      }
      return `$${(cents / 100).toFixed(2)}`;
    }
  });
}
